import { useEffect, useMemo, useState } from "react";
import {
  Row, Col, Card, Button, Select, InputNumber, Input, DatePicker,
  Form, message, Alert, Typography, Tag, Table, Tooltip, Spin
} from "antd";
import {
  PlusOutlined, DeleteOutlined, UserOutlined, EnvironmentOutlined,
  InboxOutlined, EditOutlined, ShoppingCartOutlined, IdcardOutlined, LockOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getParticipantesPorProyectoPO, getOrganizacionesPorProyectoPO
} from "../../../../services/financiero/ingresosPO/catalogosPOService";
import { objetivoPorDefecto } from "../pdf/actaData";

const { Option } = Select;
const { Text } = Typography;

const filterOption = (input, option) =>
  (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase());

const formatNumber = (value) => new Intl.NumberFormat("es-CO").format(Number(value || 0));

const createEmptyProductoRow = () => ({
  key: Date.now() + Math.random(),
  oid_producto: null,
  cantidad: null
});

const cardHeaderStyle = {
  background: "#042956",
  color: "#fff",
  borderRadius: "12px 12px 0 0",
  minHeight: 46
};

const cardStyle = {
  borderRadius: 12,
  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  marginBottom: 24,
  overflow: "hidden"
};

const CardTitle = ({ icon, children }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
    {icon}
    <span style={{ fontWeight: 600, fontSize: 14 }}>{children}</span>
  </span>
);

// Datos del beneficiario que dependen del proyecto: se limpian cada vez que
// cambia o se borra el proyecto / municipio.
const BENEFICIARIO_VACIO = {
  oid_participante: null,
  telefono_contacto_entrega: "",
  representante_legal_nombre: "",
  representante_legal_cedula: "",
  camara_comercio_codigo: "",
  camara_comercio_fecha: null
};

export default function EntregaActaForm({
  proyectos = [],
  municipiosProyecto = [],
  productos = [],
  responsables = [],
  onChange
}) {

  const [header, setHeader] = useState({
    oid_proyecto: null,
    oid_municipio: null,
    tipo_entrega: 1,
    ...BENEFICIARIO_VACIO,
    tipo_acta: "Parcial",
    fecha_entrega: dayjs(),
    lugar_entrega: "",
    vereda: "",
    objetivo: "",
    oid_usuario_entrega: null,
    oid_usuario_supervisor: null
  });

  const [productoRows, setProductoRows] = useState([createEmptyProductoRow()]);
  const [participantes, setParticipantes] = useState([]);
  const [organizaciones, setOrganizaciones] = useState([]);
  const [cargandoBeneficiarios, setCargandoBeneficiarios] = useState(false);

  const proyecto = useMemo(
    () => proyectos.find((p) => Number(p.oid) === Number(header.oid_proyecto)),
    [proyectos, header.oid_proyecto]
  );

  const municipiosDelProyecto = useMemo(
    () => municipiosProyecto.filter((m) => Number(m.oid_proyecto) === Number(header.oid_proyecto)),
    [municipiosProyecto, header.oid_proyecto]
  );

  const municipio = useMemo(
    () => municipiosDelProyecto.find((m) => Number(m.oid) === Number(header.oid_municipio)),
    [municipiosDelProyecto, header.oid_municipio]
  );

  // Sin proyecto y municipio no se habilita el beneficiario.
  const ubicacionCompleta = !!header.oid_proyecto && !!header.oid_municipio;
  const esOrganizacion = header.tipo_entrega === 2;

  const beneficiario = useMemo(() => {
    const lista = esOrganizacion ? organizaciones : participantes;
    return lista.find((b) => Number(b.oid) === Number(header.oid_participante));
  }, [esOrganizacion, organizaciones, participantes, header.oid_participante]);

  const productosDelProyecto = useMemo(
    () => productos.filter((p) =>
      Number(p.oid_proyecto) === Number(header.oid_proyecto) && Number(p.stock_disponible) > 0
    ),
    [productos, header.oid_proyecto]
  );

  const productoDe = (oid) => productosDelProyecto.find((p) => Number(p.oid) === Number(oid));

  // Beneficiarios del proyecto elegido (participantes y organizaciones).
  useEffect(() => {
    if (!header.oid_proyecto) {
      setParticipantes([]);
      setOrganizaciones([]);
      return;
    }
    let activo = true;
    setCargandoBeneficiarios(true);
    Promise.all([
      getParticipantesPorProyectoPO(header.oid_proyecto),
      getOrganizacionesPorProyectoPO(header.oid_proyecto)
    ])
      .then(([part, org]) => {
        if (!activo) return;
        setParticipantes(part || []);
        setOrganizaciones(org || []);
      })
      .catch(() => { if (activo) message.error("Error cargando los beneficiarios del proyecto"); })
      .finally(() => { if (activo) setCargandoBeneficiarios(false); });
    return () => { activo = false; };
  }, [header.oid_proyecto]);

  const updateHeader = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const handleProyectoChange = (value) => {
    const opciones = municipiosProyecto.filter((m) => Number(m.oid_proyecto) === Number(value));
    const nuevoProyecto = proyectos.find((p) => Number(p.oid) === Number(value));
    setHeader((prev) => ({
      ...prev,
      ...BENEFICIARIO_VACIO,
      oid_proyecto: value ?? null,
      // Si el proyecto solo tiene un municipio se selecciona de una vez.
      oid_municipio: value && opciones.length === 1 ? opciones[0].oid : null,
      vereda: "",
      objetivo: value ? objetivoPorDefecto(nuevoProyecto?.proyecto) : ""
    }));
    // Los productos pertenecen al proyecto: al cambiarlo se reinicia la lista.
    setProductoRows([createEmptyProductoRow()]);
  };

  const handleMunicipioChange = (value) => {
    setHeader((prev) => (
      value
        ? { ...prev, oid_municipio: value }
        : { ...prev, ...BENEFICIARIO_VACIO, oid_municipio: null, vereda: "" }
    ));
  };

  const handleTipoEntregaChange = (value) => {
    setHeader((prev) => ({ ...prev, ...BENEFICIARIO_VACIO, tipo_entrega: value }));
  };

  const handleBeneficiarioChange = (value) => {
    if (!value) {
      setHeader((prev) => ({ ...prev, ...BENEFICIARIO_VACIO }));
      return;
    }
    if (esOrganizacion) {
      setHeader((prev) => ({
        ...prev,
        ...BENEFICIARIO_VACIO,
        oid_participante: value
      }));
    } else {
      const part = participantes.find((p) => Number(p.oid) === Number(value));
      setHeader((prev) => ({
        ...prev,
        ...BENEFICIARIO_VACIO,
        oid_participante: value,
        telefono_contacto_entrega: part?.telefono || "",
        vereda: part?.vereda || prev.vereda
      }));
    }
  };

  const handleProductoRowChange = (rowKey, productoId) => {
    setProductoRows((rows) => rows.map((row) =>
      row.key === rowKey ? { ...row, oid_producto: productoId ?? null, cantidad: productoId ? 1 : null } : row
    ));
  };

  const handleCantidadRowChange = (rowKey, cantidad) => {
    setProductoRows((rows) => rows.map((row) => (row.key === rowKey ? { ...row, cantidad } : row)));
  };

  const addProductoRow = () => setProductoRows((rows) => [...rows, createEmptyProductoRow()]);

  const removeProductoRow = (key) => {
    setProductoRows((rows) => (rows.length === 1 ? rows : rows.filter((r) => r.key !== key)));
  };

  // Regla de entregas: no se puede entregar más de lo disponible en stock.
  const errorDeFila = (row) => {
    if (!row.oid_producto) return { oid_producto: "Seleccione el producto" };
    const disponible = Number(productoDe(row.oid_producto)?.stock_disponible || 0);
    if (!row.cantidad || Number(row.cantidad) <= 0) return { cantidad: "Cantidad inválida" };
    if (Number(row.cantidad) > disponible) return { cantidad: `Máximo ${formatNumber(disponible)}` };
    return null;
  };

  const seleccionados = productoRows.map((r) => Number(r.oid_producto)).filter(Boolean);
  const totalLineas = productoRows.filter((r) => r.oid_producto && r.cantidad).length;
  const totalCantidad = productoRows.reduce((acc, r) => acc + Number(r.cantidad || 0), 0);

  // Entrega al padre lo que necesita para guardar y para generar el PDF.
  useEffect(() => {
    const entrega = responsables.find((r) => Number(r.oid) === Number(header.oid_usuario_entrega));
    const supervisor = responsables.find((r) => Number(r.oid) === Number(header.oid_usuario_supervisor));
    const fecha = header.fecha_entrega ? header.fecha_entrega.format("YYYY-MM-DD") : null;

    const headerOk =
      ubicacionCompleta &&
      !!header.oid_participante &&
      !!fecha &&
      !!header.oid_usuario_entrega &&
      !!header.oid_usuario_supervisor;
    const rowsOk = productoRows.length > 0 && productoRows.every((row) => !errorDeFila(row));

    onChange?.({
      isValid: headerOk && rowsOk,
      esOrganizacion,
      encabezado: {
        oid_proyecto: header.oid_proyecto,
        oid_municipio: header.oid_municipio,
        tipo_entrega: header.tipo_entrega,
        oid_participante: header.oid_participante,
        tipo_acta: header.tipo_acta,
        fecha_entrega: fecha,
        lugar_entrega: header.lugar_entrega,
        vereda: header.vereda,
        objetivo: header.objetivo,
        oid_usuario_entrega: header.oid_usuario_entrega,
        oid_usuario_supervisor: header.oid_usuario_supervisor,
        responsable: entrega?.name || null,
        telefono_contacto_entrega: header.telefono_contacto_entrega,
        representante_legal_nombre: header.representante_legal_nombre,
        representante_legal_cedula: header.representante_legal_cedula,
        camara_comercio_codigo: header.camara_comercio_codigo,
        camara_comercio_fecha: header.camara_comercio_fecha ? header.camara_comercio_fecha.format("YYYY-MM-DD") : null
      },
      lineas: productoRows
        .filter((r) => r.oid_producto && r.cantidad)
        .map((r) => ({ oid_producto: r.oid_producto, cantidad: Number(r.cantidad) })),
      // Mismos nombres de campo que devuelve el listado de actas, para que el
      // PDF se arme igual al descargarlo recién guardado o después.
      actaPDF: {
        tipo_entrega: header.tipo_entrega,
        oid_participante: header.oid_participante,
        fecha_entrega: fecha,
        tipo_acta: header.tipo_acta,
        proyecto: proyecto?.proyecto,
        departamento: municipio?.departamento,
        municipio: municipio?.municipio,
        lugar_entrega: header.lugar_entrega,
        telefono_contacto_entrega: header.telefono_contacto_entrega,
        nombre_entrega: esOrganizacion ? beneficiario?.organizacion : beneficiario?.nombre_participante,
        identificacion: esOrganizacion ? beneficiario?.Nit : beneficiario?.numero_identificacion,
        sigla: esOrganizacion ? beneficiario?.sigla : null,
        representante_legal_nombre: header.representante_legal_nombre,
        representante_legal_cedula: header.representante_legal_cedula,
        usuario_entrega_nombre: entrega?.name,
        usuario_supervisor_nombre: supervisor?.name
      },
      lineasPDF: productoRows
        .filter((r) => r.oid_producto && r.cantidad)
        .map((r) => ({ ...productoDe(r.oid_producto), cantidad: r.cantidad })),
      extrasPDF: { vereda: header.vereda, objetivo: header.objetivo }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header, productoRows, participantes, organizaciones, productos, responsables]);

  const columnasProductos = [
    {
      title: "#",
      width: 48,
      align: "center",
      render: (_, __, index) => <Text strong>{index + 1}</Text>
    },
    {
      title: "Producto",
      render: (_, row) => {
        const error = row.oid_producto ? null : errorDeFila(row)?.oid_producto;
        return (
          <Form.Item validateStatus={error && productoRows.length > 1 ? "error" : ""} style={{ marginBottom: 0 }}>
            <Select
              showSearch
              allowClear
              disabled={!header.oid_proyecto}
              placeholder={header.oid_proyecto ? "Seleccione producto con stock" : "Primero seleccione el proyecto"}
              value={row.oid_producto}
              onChange={(v) => handleProductoRowChange(row.key, v)}
              filterOption={filterOption}
              style={{ width: "100%" }}
              notFoundContent="No hay productos con stock para este proyecto"
            >
              {productosDelProyecto.map((p) => (
                <Option
                  key={p.oid}
                  value={p.oid}
                  disabled={seleccionados.includes(Number(p.oid)) && Number(p.oid) !== Number(row.oid_producto)}
                >
                  {`${p.nombre_corto || p.nombre_producto} — PO ${p.Numero_PO}`}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );
      }
    },
    {
      title: "Unidad",
      width: 130,
      render: (_, row) => <Text type="secondary">{productoDe(row.oid_producto)?.unidad_medida || "—"}</Text>
    },
    {
      title: "Disponible",
      width: 120,
      align: "center",
      render: (_, row) => {
        const p = productoDe(row.oid_producto);
        if (!p) return <Text type="secondary">—</Text>;
        const restante = Number(p.stock_disponible) - Number(row.cantidad || 0);
        return (
          <Tooltip title={`Stock actual: ${formatNumber(p.stock_disponible)} · Queda después de esta entrega: ${formatNumber(Math.max(restante, 0))}`}>
            <Tag color={restante < 0 ? "red" : restante === 0 ? "orange" : "green"} style={{ marginRight: 0 }}>
              {formatNumber(p.stock_disponible)}
            </Tag>
          </Tooltip>
        );
      }
    },
    {
      title: "Cantidad",
      width: 150,
      render: (_, row) => {
        const error = row.oid_producto ? errorDeFila(row)?.cantidad : null;
        const disponible = Number(productoDe(row.oid_producto)?.stock_disponible || 0);
        return (
          <Form.Item validateStatus={error ? "error" : ""} help={error} style={{ marginBottom: 0 }}>
            <InputNumber
              min={0}
              max={disponible || undefined}
              style={{ width: "100%" }}
              value={row.cantidad}
              disabled={!row.oid_producto}
              onChange={(v) => handleCantidadRowChange(row.key, v)}
            />
          </Form.Item>
        );
      }
    },
    {
      title: "",
      width: 52,
      align: "center",
      render: (_, row) => (
        <Tooltip title={productoRows.length > 1 ? "Quitar producto" : "Debe quedar al menos un producto"}>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={productoRows.length <= 1}
            onClick={() => removeProductoRow(row.key)}
          />
        </Tooltip>
      )
    }
  ];

  return (
    <div style={{ paddingBottom: 8 }}>
      <Form layout="vertical" autoComplete="off">

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle, body: { padding: 24 } }}
          title={<CardTitle icon={<EnvironmentOutlined />}>1. Proyecto y ubicación</CardTitle>}
        >
          <Row gutter={[24, 0]}>
            <Col xs={24} md={12}>
              <Form.Item label="Proyecto" required tooltip="Defina primero el proyecto: de él dependen los municipios, los beneficiarios y los productos disponibles.">
                <Select
                  size="large"
                  showSearch
                  allowClear
                  placeholder="Seleccione el proyecto"
                  value={header.oid_proyecto}
                  onChange={handleProyectoChange}
                  filterOption={filterOption}
                >
                  {proyectos.map((p) => (
                    <Option key={p.oid} value={p.oid}>
                      {p.codigo} - {p.proyecto}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Municipio" required tooltip="Solo los municipios asociados al proyecto">
                <Select
                  size="large"
                  showSearch
                  allowClear
                  disabled={!header.oid_proyecto}
                  value={header.oid_municipio}
                  onChange={handleMunicipioChange}
                  filterOption={filterOption}
                  placeholder={header.oid_proyecto ? "Seleccione municipio" : "Primero seleccione el proyecto"}
                  notFoundContent="El proyecto no tiene municipios asociados"
                >
                  {municipiosDelProyecto.map((m) => (
                    <Option key={m.oid} value={m.oid}>{m.municipio}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Departamento" tooltip="Se completa solo según el municipio elegido.">
                <Input size="large" readOnly value={municipio?.departamento || ""} placeholder="Según el municipio" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle, body: { padding: 24 } }}
          title={<CardTitle icon={<UserOutlined />}>2. Beneficiario</CardTitle>}
        >
          {!ubicacionCompleta ? (
            <Alert
              type="info"
              showIcon
              icon={<LockOutlined />}
              style={{ borderRadius: 10 }}
              message="Seleccione primero el proyecto y el municipio"
              description="El beneficiario se habilita cuando la sección de proyecto y ubicación está completa, y solo se listan los participantes u organizaciones de ese proyecto."
            />
          ) : (
            <Spin spinning={cargandoBeneficiarios}>
              <Row gutter={[24, 0]}>
                <Col xs={24} md={6}>
                  <Form.Item label="Tipo de entrega" required tooltip="Participante genera el acta individual; Organización genera el acta de organizaciones.">
                    <Select size="large" value={header.tipo_entrega} onChange={handleTipoEntregaChange}>
                      <Option value={1}>Participante</Option>
                      <Option value={2}>Organización</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={esOrganizacion ? 18 : 12}>
                  <Form.Item
                    label="Beneficiario"
                    required
                    extra={`${esOrganizacion ? organizaciones.length : participantes.length} ${esOrganizacion ? "organización(es)" : "participante(s)"} en el proyecto ${proyecto?.codigo || ""}`}
                  >
                    <Select
                      size="large"
                      showSearch
                      allowClear
                      placeholder={esOrganizacion ? "Buscar organización por nombre o sigla" : "Buscar participante por nombre o identificación"}
                      value={header.oid_participante}
                      onChange={handleBeneficiarioChange}
                      filterOption={filterOption}
                      notFoundContent={esOrganizacion ? "El proyecto no tiene organizaciones" : "El proyecto no tiene participantes"}
                    >
                      {esOrganizacion
                        ? organizaciones.map((o) => (
                          <Option key={o.oid} value={o.oid}>
                            {[o.organizacion, o.sigla, o.Nit].filter(Boolean).join(" - ")}
                          </Option>
                        ))
                        : participantes.map((p) => (
                          <Option key={p.oid} value={p.oid}>
                            {`${p.nombre_participante} - ${p.numero_identificacion}`}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                {!esOrganizacion && (
                  <Col xs={24} md={6}>
                    <Form.Item label="Teléfono de contacto">
                      <Input
                        size="large"
                        maxLength={20}
                        value={header.telefono_contacto_entrega}
                        onChange={(e) => updateHeader("telefono_contacto_entrega", e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              {esOrganizacion && (
                <>
                  <Alert
                    type="warning"
                    showIcon
                    icon={<IdcardOutlined />}
                    message="Datos para el acta de organización"
                    description="Se capturan por cada acta y salen del certificado de cámara de comercio vigente."
                    style={{ marginBottom: 20, borderRadius: 10 }}
                  />
                  <Row gutter={[24, 0]}>
                    <Col xs={24} md={12}>
                      <Form.Item label="Representante legal">
                        <Input
                          size="large"
                          maxLength={150}
                          value={header.representante_legal_nombre}
                          onChange={(e) => updateHeader("representante_legal_nombre", e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Cédula representante legal">
                        <Input
                          size="large"
                          maxLength={20}
                          value={header.representante_legal_cedula}
                          onChange={(e) => updateHeader("representante_legal_cedula", e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Código cámara comercio">
                        <Input
                          size="large"
                          maxLength={50}
                          value={header.camara_comercio_codigo}
                          onChange={(e) => updateHeader("camara_comercio_codigo", e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Fecha certificado">
                        <DatePicker
                          size="large"
                          style={{ width: "100%" }}
                          format="DD/MM/YYYY"
                          value={header.camara_comercio_fecha}
                          onChange={(v) => updateHeader("camara_comercio_fecha", v)}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Teléfono de contacto">
                        <Input
                          size="large"
                          maxLength={20}
                          value={header.telefono_contacto_entrega}
                          onChange={(e) => updateHeader("telefono_contacto_entrega", e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
            </Spin>
          )}
        </Card>

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle, body: { padding: 24 } }}
          title={<CardTitle icon={<InboxOutlined />}>3. Entrega</CardTitle>}
        >
          <Row gutter={[24, 0]}>
            <Col xs={24} md={6}>
              <Form.Item label="Tipo de acta" required tooltip="Parcial si aún quedan entregas por hacer al beneficiario; Final si con esta se completa.">
                <Select size="large" value={header.tipo_acta} onChange={(v) => updateHeader("tipo_acta", v)}>
                  <Option value="Parcial">Parcial</Option>
                  <Option value="Final">Final</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Fecha de entrega" required tooltip="Fecha en que se firma y entrega. Las entregas del mismo beneficiario en la misma fecha forman una sola acta.">
                <DatePicker
                  size="large"
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  allowClear={false}
                  value={header.fecha_entrega}
                  onChange={(v) => updateHeader("fecha_entrega", v)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Vereda" tooltip="Sale en el acta. Se guarda con la entrega para no volver a pedirla al descargar el PDF.">
                <Input
                  size="large"
                  placeholder="Ej: San Clemente"
                  value={header.vereda}
                  onChange={(e) => updateHeader("vereda", e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Lugar de entrega" tooltip="Sitio donde se hace la entrega. Sale en la fila Lugar de entrega del acta.">
                <Input
                  size="large"
                  maxLength={150}
                  placeholder="Ej: Vereda Pueblo Nuevo"
                  value={header.lugar_entrega}
                  onChange={(e) => updateHeader("lugar_entrega", e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Objetivo del acta"
                tooltip="Texto del apartado OBJETIVO del acta. Se guarda con la entrega; se propone uno al elegir el proyecto y puede ajustarlo."
                style={{ marginBottom: 0 }}
              >
                <Input.TextArea
                  rows={2}
                  placeholder="Se propone uno al elegir el proyecto; puede ajustarlo"
                  value={header.objetivo}
                  onChange={(e) => updateHeader("objetivo", e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle, body: { padding: 24 } }}
          title={<CardTitle icon={<EditOutlined />}>4. Firmas</CardTitle>}
        >
          <Row gutter={[24, 0]}>
            <Col xs={24} md={12}>
              <Form.Item label="Quien entrega (Equipo Técnico FAO)" required tooltip="Persona del equipo técnico que firma la entrega en el acta." style={{ marginBottom: 0 }}>
                <Select
                  size="large"
                  showSearch
                  placeholder="Seleccione el responsable de la entrega"
                  value={header.oid_usuario_entrega}
                  onChange={(v) => updateHeader("oid_usuario_entrega", v)}
                  filterOption={filterOption}
                >
                  {responsables.map((r) => (
                    <Option key={r.oid} value={r.oid}>{r.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Supervisor Técnico Local FAO" required tooltip="Supervisor que firma el acta junto con quien entrega." style={{ marginBottom: 0 }}>
                <Select
                  size="large"
                  showSearch
                  placeholder="Seleccione el supervisor"
                  value={header.oid_usuario_supervisor}
                  onChange={(v) => updateHeader("oid_usuario_supervisor", v)}
                  filterOption={filterOption}
                >
                  {responsables.map((r) => (
                    <Option key={r.oid} value={r.oid}>{r.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          style={{ ...cardStyle, marginBottom: 0 }}
          styles={{ header: cardHeaderStyle, body: { padding: 0 } }}
          title={<CardTitle icon={<ShoppingCartOutlined />}>5. Productos entregados</CardTitle>}
          extra={totalLineas > 0 && (
            <Tag color="blue" style={{ marginRight: 0 }}>
              {totalLineas} producto{totalLineas > 1 ? "s" : ""} · {formatNumber(totalCantidad)} unidades
            </Tag>
          )}
        >
          {header.oid_proyecto && !productosDelProyecto.length && (
            <Alert
              type="warning"
              showIcon
              banner
              message="No hay productos con stock disponible para el proyecto seleccionado."
            />
          )}
          <Table
            rowKey="key"
            size="small"
            dataSource={productoRows}
            columns={columnasProductos}
            pagination={false}
          />
          <div style={{ padding: 16 }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addProductoRow}
              disabled={!header.oid_proyecto}
              block
              style={{ height: 42, borderRadius: 8 }}
            >
              Agregar producto
            </Button>
          </div>
        </Card>
      </Form>
    </div>
  );
}
